import axios from "axios";
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from "axios";
import { useEnv } from "@/hooks/useEnv";
import { ElMessage } from "element-plus";
import { getCurrentLanguage } from "@/hooks/useLanguageEnum";
import router from "@/router/route";

// 接口响应格式
interface ApiResponse<T = unknown> {
  code: number;
  data: T;
  msg: string;
}

// Token 数据结构
interface TokenData {
  accessToken: string;
  refreshToken: string;
  accessTokenExpire: number; // 秒数
  refreshTokenExpire: number; // 秒数
}

// 扩展请求配置
export interface RequestConfig extends AxiosRequestConfig {
  // 是否显示加载提示
  showLoading?: boolean;
  // 是否处理错误（显示错误提示）
  handleError?: boolean;
  // 是否重试
  retry?: boolean;
  // 是否跳过认证（用于不需要token的请求，如登录、刷新token、注册等）
  skipAuth?: boolean;
  // 是否跳过错误处理
  skipErrorHandler?: boolean;
  // 是否跳过自动刷新检查（用于刷新token请求本身）
  skipAutoRefresh?: boolean;
  // 请求标识（用于取消特定请求）
  requestId?: string;
  // 请求组标识（用于取消一组请求）
  requestGroup?: string;
}

// 封装请求类
class Request {
  // Axios 实例
  private instance: AxiosInstance;
  // 基础配置
  private baseConfig: AxiosRequestConfig = {
    timeout: 1000 * 60 * 10,
    headers: {
      "Content-Type": "application/json;charset=UTF-8"
    }
  };

  // 刷新 token 标志
  private isRefreshing = false;
  // 重试请求队列
  private requestQueue: ((token: string) => void)[] = [];
  // 刷新防抖时间（毫秒）
  private readonly REFRESH_DEBOUNCE = 10000;

  // 请求取消控制器映射
  private cancelControllers: Map<string, AbortController> = new Map();
  // 请求组取消控制器映射
  private groupCancelControllers: Map<string, AbortController> = new Map();

  constructor(config?: AxiosRequestConfig) {
    // 动态获取基础URL
    this.baseConfig.baseURL = useEnv().baseUrl;

    // 创建实例
    this.instance = axios.create(Object.assign({}, this.baseConfig, config));

    // 请求拦截器
    this.instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig & RequestConfig) => {
        // 添加语言头
        if (config.headers) {
          config.headers["Language-Code"] = getCurrentLanguage();
        }

        // 创建取消控制器
        const controller = new AbortController();

        // 设置请求标识的取消控制器
        if (config.requestId) {
          // 如果已有相同ID的请求，先取消
          this.cancelRequest(config.requestId);
          this.cancelControllers.set(config.requestId, controller);
        }

        // 设置请求组标识的取消控制器
        if (config.requestGroup) {
          // 创建或更新组控制器
          const groupController = new AbortController();
          this.groupCancelControllers.set(config.requestGroup, groupController);
          config.signal = groupController.signal;
        } else {
          // 使用单个请求的取消信号
          config.signal = controller.signal;
        }

        // 跳过认证的特殊请求（如登录、刷新token、公开接口等）
        if (config.skipAuth) {
          return config;
        }

        // 检查访问令牌是否有效（未过期或即将过期），若即将过期则尝试刷新
        if (this.isAccessTokenExpiring()) {
          // 刷新令牌
          try {
            const newAccessToken = await this.refreshTokenIfNeeded();
            if (newAccessToken) {
              // 更新请求头中的令牌
              if (config.headers) {
                config.headers.Authorization = `${newAccessToken}`;
                config.headers.token = `${newAccessToken}`;
              }
            }
          } catch (error) {
            // 刷新失败，继续使用当前令牌（后续请求可能会失败，但会在响应拦截器中处理）
            console.error("自动刷新令牌失败，继续发送请求", error);
            return Promise.reject(new Error("令牌刷新失败"));
          }
        }

        // 添加访问令牌到请求头
        const accessToken = this.getAccessToken();
        if (accessToken && config.headers) {
          config.headers.Authorization = `${accessToken}`;
          config.headers.token = `${accessToken}`;
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse | Blob>) => {
        const config = response.config as RequestConfig;

        // 请求完成后清除取消控制器
        if (config.requestId) {
          this.cancelControllers.delete(config.requestId);
        }

        // 如果是 blob 响应，直接返回
        if (response.config.responseType === "blob" || response.data instanceof Blob) {
          return response;
        }

        const { code, msg } = response.data as ApiResponse;

        // 处理业务响应码401 (Token过期)
        if (code === 401) {
          // 对于需要认证的请求，处理令牌过期
          // if (!config.skipAuth) {
          //   this.handleTokenExpired();
          // }
          this.handleTokenExpired();
          return Promise.reject(response);
        }

        // 成功响应
        if (code === 200) {
          return response;
        } else {
          // 处理其他错误状态码
          this.handleErrorCode(code, msg, config);
          return Promise.reject(response);
        }
      },
      async (error: AxiosError) => {
        const config = error.config as RequestConfig;

        // 请求完成后清除取消控制器
        if (config?.requestId) {
          this.cancelControllers.delete(config.requestId);
        }

        // 如果是取消请求的错误，直接抛出
        if (axios.isCancel(error)) {
          return Promise.reject(new Error("请求已被取消"));
        }

        const response = error.response;

        // 处理HTTP状态码401 (Token无效或缺失)
        if (response?.status === 401) {
          // 对于需要认证的请求，处理令牌过期
          // if (!config?.skipAuth) {
          //   this.handleTokenExpired();
          // }

          // -------------------------
          // 检查是否正在刷新Token
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.requestQueue.push(() => {
                resolve(this.instance(error.config)); // 重试原请求
              });
            });
          }
          // -------------------------

          this.handleTokenExpired();
          return Promise.reject(error);
        }

        // 处理其他错误
        if (config?.handleError && !config?.skipErrorHandler) {
          const errorMessage = (response?.data as any)?.msg || error.message || "请求失败";
          this.showError(errorMessage, config);
        }

        return Promise.reject(error);
      }
    );
  }

  // 安全获取localStorage
  private getSafeStorage(key: string): string | null {
    if (typeof window !== "undefined" && window.localStorage) {
      return localStorage.getItem(key);
    }
    return null;
  }

  // 获取访问令牌
  private getAccessToken(): string | null {
    return this.getSafeStorage("accessToken");
  }

  // 获取刷新令牌
  private getRefreshToken(): string | null {
    return this.getSafeStorage("refreshToken");
  }

  // 获取访问令牌过期时间（毫秒时间戳）
  private getAccessTokenExpireAt(): number {
    const expireAt = this.getSafeStorage("accessTokenExpireAt");
    return expireAt ? parseInt(expireAt) : 0;
  }

  // 获取刷新令牌过期时间（毫秒时间戳）
  private getRefreshTokenExpireAt(): number {
    const expireAt = this.getSafeStorage("refreshTokenExpireAt");
    return expireAt ? parseInt(expireAt) : 0;
  }

  // 检查访问令牌是否即将过期（提前5分钟）
  private isAccessTokenExpiring(): boolean {
    const expireAt = this.getAccessTokenExpireAt();
    if (!expireAt) return true; // 如果没有存储过期时间，视为过期

    const currentTime = Date.now();
    // 如果过期时间减去当前时间小于5分钟（300000毫秒），则视为即将过期
    // console.log(
    //   `检查访问令牌是否即将过期：expireAt:${expireAt},currentTime:${currentTime},${
    //     expireAt - currentTime < 300000
    //   }`
    // );
    return expireAt - currentTime < 300000;
  }

  // 检查刷新令牌是否有效
  private isRefreshTokenValid(): boolean {
    const expireAt = this.getRefreshTokenExpireAt();
    // console.log(
    //   `检查刷新令牌是否有效：expireAt:${expireAt},Date.now():${Date.now()},${expireAt > Date.now()}`
    // );
    return expireAt > Date.now();
  }

  // 存储令牌数据
  private storeTokenData(data: TokenData): void {
    if (typeof window !== "undefined" && window.localStorage) {
      const now = Date.now();
      // 存储令牌字符串
      if (data.accessTokenExpire) {
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem(
          "personal-info",
          JSON.stringify({
            token: data.accessToken
          })
        );
      }
      if (data.refreshToken) {
        localStorage.setItem("refreshToken", data.refreshToken);
      }
      // 存储绝对过期时间（当前时间+有效期（秒）*1000 -> 毫秒时间戳）
      if (data.accessTokenExpire) {
        localStorage.setItem("accessTokenExpireAt", String(now + data.accessTokenExpire * 1000));
      }
      if (data.refreshTokenExpire) {
        localStorage.setItem("refreshTokenExpireAt", String(now + data.refreshTokenExpire * 1000));
      }
    }
  }

  // 清除认证信息
  private clearAuthData(): void {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("accessTokenExpireAt");
      localStorage.removeItem("refreshTokenExpireAt");
      localStorage.removeItem("personal-info");
    }
  }

  // 处理令牌过期（跳转到登录页）
  private handleTokenExpired() {
    this.clearAuthData();
    // 使用路由跳转（避免硬编码路径）
    const loginType = localStorage.getItem("loginType");
    if (router && router.currentRoute.value.path !== "/login") {
      router.replace("/login");
    } else if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      router.replace("/login");
    }
  }

  // 刷新令牌（如果需要）
  private async refreshTokenIfNeeded(): Promise<string | null> {
    // 如果刷新令牌无效，直接跳转登录
    if (!this.isRefreshTokenValid()) {
      this.handleTokenExpired();
      return null;
    }

    // 防止并发刷新
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.requestQueue.push((token: string) => {
          resolve(token);
        });
      });
    }

    this.isRefreshing = true;

    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error("刷新令牌不存在");
      }

      // 调用刷新令牌接口 - 注意：此请求需要跳过认证和自动刷新检查
      const loginType = localStorage.getItem("loginType");
      const response = await this.instance.post<ApiResponse<TokenData>>(
        loginType === "student" ? "/api/student/auth/refreshToken" : "/api/user/refreshToken",
        {},
        {
          skipAuth: true, // 跳过认证（不携带当前accessToken）
          skipAutoRefresh: true, // 跳过自动刷新检查（避免递归）
          skipErrorHandler: true, // 跳过全局错误处理（自己处理错误）
          headers: {
            Authorization: `${refreshToken}`,
            "Language-Code": getCurrentLanguage() // 添加语言头
          }
        } as RequestConfig
      );

      if (response.data.code === 200) {
        const tokenData = response.data.data;
        // 存储新的令牌数据
        this.storeTokenData(tokenData);

        // 执行队列中的请求
        this.requestQueue.forEach((callback) => callback(tokenData.accessToken));
        this.requestQueue = [];

        return tokenData.accessToken;
      } else {
        throw new Error(response.data.msg || "令牌刷新失败");
      }
    } catch (error) {
      // 刷新失败，清除令牌并跳转登录
      this.handleTokenExpired();
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  // 处理错误状态码
  private handleErrorCode(code: number, msg: string, config?: RequestConfig) {
    // 统一错误处理 (跳过401，因为已在拦截器中处理)
    if (code !== 401 && !config?.skipErrorHandler) {
      this.showError(msg, config);
    }

    // 特殊状态码处理
    switch (code) {
      case 403:
        console.warn("无权限访问:", msg);
        break;
      case 404:
        console.warn("资源不存在:", msg);
        break;
      case 500:
        console.error("服务器错误:", msg);
        break;
      default:
        console.warn(`状态码 ${code}:`, msg);
    }
  }

  // 显示错误提示
  private showError(msg: string, config?: RequestConfig) {
    // 优先使用配置中的错误处理
    if (config?.handleError === false) return;

    // 使用Element Plus消息提示
    if (typeof ElMessage !== "undefined") {
      ElMessage.warning(msg);
    } else {
      // 降级处理
      console.error("请求错误:", msg);
      if (typeof window !== "undefined") {
        alert(`请求错误: ${msg}`);
      }
    }
  }

  // 取消单个请求
  public cancelRequest(requestId: string, reason?: string) {
    const controller = this.cancelControllers.get(requestId);
    if (controller) {
      controller.abort(reason || `请求 ${requestId} 已被取消`);
      this.cancelControllers.delete(requestId);
    }
  }

  // 取消请求组的所有请求
  public cancelRequestGroup(groupId: string, reason?: string) {
    const controller = this.groupCancelControllers.get(groupId);
    if (controller) {
      controller.abort(reason || `请求组 ${groupId} 已被取消`);
      this.groupCancelControllers.delete(groupId);
    }
  }

  // 取消所有待处理请求
  public cancelAllPendingRequests(reason?: string) {
    // 取消所有单个请求
    this.cancelControllers.forEach((controller) => {
      controller.abort(reason || "所有请求已被取消");
    });
    this.cancelControllers.clear();

    // 取消所有请求组
    this.groupCancelControllers.forEach((controller) => {
      controller.abort(reason || "所有请求组已被取消");
    });
    this.groupCancelControllers.clear();
  }

  // 公共请求方法
  public async request<T = unknown>(config: RequestConfig): Promise<ApiResponse<T>> {
    // 深度合并 headers
    if (config.headers) {
      config.headers = {
        ...this.baseConfig.headers,
        ...config.headers
      };
    }

    try {
      const response = await this.instance.request<ApiResponse<T>>(config);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  // GET 请求
  public async get<T = unknown>(
    url: string,
    params?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    // 合并参数，添加默认时间戳 t
    const mergedParams = {
      t: Date.now(),
      ...params
    };
    return this.request({ ...config, method: "GET", url, params: mergedParams });
  }

  // POST 请求
  public async post<T = unknown>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request({ ...config, method: "POST", url, data });
  }

  // PUT 请求
  public async put<T = unknown>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request({ ...config, method: "PUT", url, data });
  }

  // DELETE 请求
  public async delete<T = unknown>(
    url: string,
    config?: RequestConfig,
    data?: any
  ): Promise<ApiResponse<T>> {
    return this.request({ ...config, method: "DELETE", url, data });
  }

  // PATCH 请求
  public async patch<T = unknown>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> {
    return this.request({ ...config, method: "PATCH", url, data });
  }
}

// 创建请求实例
const request = new Request();

// 兼容原有的 useFetch 方法
export const useFetch = (options: {
  url: string;
  data?: any;
  config?: RequestConfig;
}): Promise<ApiResponse> => {
  const { url, data, config = {} } = options;

  // 根据请求方法决定参数传递方式
  const method = config.method?.toUpperCase() || "GET";

  if (method === "GET") {
    // 合并参数 - 避免覆盖已有params
    return request.get(url, { ...data, ...config.params }, config);
  } else {
    return request.post(url, data, config);
  }
};

export default request;
